import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { AudienceDetailsPresenter } from '@/components/audiences/audience-details.presenter';
import {
  mapAudienceCampaignAnalytics,
  mapAudienceDtoToDetails,
} from '@/components/audiences/map-audience-dto-to-details';
import { mapAudienceMemberToCustomer } from '@/components/audiences/map-audience-member-to-customer';
import { toast } from '@/components/ui/sonner';
import {
  useArchiveAudience,
  useAudience,
  useAudienceCampaigns,
  useAudienceMembers,
  useAudiencePreview,
  useDeleteAudience,
} from '@/hooks/use-audiences';
import {
  currencyForDisplay,
  useStoreCurrency,
} from '@/hooks/use-store-currency';
import {
  CenteredContent,
  CenteredContentLayout,
  Header,
} from '@/layouts/centered-content.layout';
import { SidebarLayout } from '@/layouts/sidebar.layout';
import { PageBreadcrumb } from '@/components/page-breadcrumb';
import { ChevronLeft } from 'lucide-react';
import { Link } from 'react-router';

const CUSTOMERS_PAGE_SIZE = 10;

export default function AudienceDetailsPage() {
  const tenantSlug = useParams().tenantSlug ?? '';
  const audienceId = useParams().audienceId ?? '';
  const navigate = useNavigate();
  const { LL, locale } = useI18nContext();
  const [customersPage, setCustomersPage] = useState(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const audienceQuery = useAudience(audienceId);
  const membersQuery = useAudienceMembers(audienceId, {
    limit: CUSTOMERS_PAGE_SIZE,
    offset: (customersPage - 1) * CUSTOMERS_PAGE_SIZE,
  });
  const previewQuery = useAudiencePreview(
    audienceQuery.data?.type === 'filter'
      ? audienceQuery.data.filters
      : undefined
  );
  const audienceCampaignsQuery = useAudienceCampaigns(
    audienceId,
    deleteDialogOpen
  );
  const deleteAudience = useDeleteAudience();
  const archiveAudience = useArchiveAudience();

  const currency = currencyForDisplay(useStoreCurrency());

  const campaignMetrics = useMemo(
    () =>
      audienceQuery.data
        ? mapAudienceCampaignAnalytics(audienceQuery.data)
        : null,
    [audienceQuery.data]
  );

  const dependentCampaignCount = audienceCampaignsQuery.data?.length ?? 0;
  const removeMode =
    dependentCampaignCount > 0 && !audienceCampaignsQuery.isLoading
      ? 'archive'
      : 'delete';

  const audience = useMemo(() => {
    if (!audienceQuery.data) return null;
    return mapAudienceDtoToDetails(audienceQuery.data);
  }, [audienceQuery.data]);

  const hasPreviewableFilters =
    audienceQuery.data?.type === 'filter' && !!audienceQuery.data.filters;
  const donutTargetUsersCount = previewQuery.isLoading
    ? undefined
    : previewQuery.data?.targetedUserCount;
  const donutTotalUserbaseCount =
    previewQuery.data?.notificationsEnabledUserCount ?? 0;
  const isDonutLoading = hasPreviewableFilters && previewQuery.isLoading;

  const customers = useMemo(
    () => (membersQuery.data?.data ?? []).map(mapAudienceMemberToCustomer),
    [membersQuery.data?.data]
  );

  const handleRemoveConfirm = async () => {
    const audienceName = audience?.name ?? '';

    try {
      if (removeMode === 'archive') {
        await archiveAudience.mutateAsync(audienceId);
        toast.success(LL.audiences.details.actions.audienceArchivedTitle(), {
          description: LL.audiences.details.actions.audienceArchivedDescription(
            { audienceName }
          ),
        });
      } else {
        await deleteAudience.mutateAsync(audienceId);
        toast.success(LL.audiences.details.actions.audienceDeletedTitle(), {
          description: LL.audiences.details.actions.audienceDeletedDescription({
            audienceName,
          }),
        });
      }
      navigate(`/${tenantSlug}/audiences`);
    } catch {
      toast.error(
        removeMode === 'archive'
          ? LL.audiences.details.actions.failedToArchiveAudience()
          : LL.audiences.details.actions.failedToDeleteAudience()
      );
    }
  };

  if (audienceQuery.isLoading) {
    return (
      <SidebarLayout className="!p-0">
        <CenteredContentLayout>
          <Header className="flex justify-between w-full" rightGutter>
            <div className="flex flex-col gap-2">
              <PageBreadcrumb
                items={[
                  {
                    label: LL.audiences.title(),
                    href: `/${tenantSlug}/audiences`,
                  },
                  { label: LL.audiences.details.breadcrumbLabel() },
                ]}
              />
            </div>
          </Header>
          <CenteredContent>
            <div className="py-10 text-center">
              <p className="text-muted-foreground">
                {LL.audiences.loadingAudience()}
              </p>
            </div>
          </CenteredContent>
        </CenteredContentLayout>
      </SidebarLayout>
    );
  }

  if (audienceQuery.isError || !audience || !campaignMetrics) {
    return (
      <SidebarLayout className="!p-0">
        <CenteredContentLayout>
          <Header className="flex justify-between w-full" rightGutter>
            <div className="flex flex-col gap-2">
              <PageBreadcrumb
                items={[
                  {
                    label: LL.audiences.title(),
                    href: `/${tenantSlug}/audiences`,
                  },
                  { label: LL.audiences.details.breadcrumbLabel() },
                ]}
              />
              <div className="flex min-w-0 items-center gap-2 sm:gap-4">
                <Link
                  to={`/${tenantSlug}/audiences`}
                  className="shrink-0"
                  aria-label={LL.audiences.title()}
                >
                  <ChevronLeft className="size-6" />
                </Link>
              </div>
            </div>
          </Header>
          <CenteredContent>
            <div className="py-10 text-center">
              <p className="text-muted-foreground">
                {LL.audiences.failedToLoadAudience()}
              </p>
            </div>
          </CenteredContent>
        </CenteredContentLayout>
      </SidebarLayout>
    );
  }

  return (
    <AudienceDetailsPresenter
      audience={audience}
      donutTargetUsersCount={donutTargetUsersCount}
      donutTotalUserbaseCount={donutTotalUserbaseCount}
      isDonutLoading={isDonutLoading}
      campaignMetrics={campaignMetrics}
      currency={currency}
      customers={customers}
      customersTotal={membersQuery.data?.total ?? 0}
      customersPage={customersPage}
      customersPageSize={CUSTOMERS_PAGE_SIZE}
      onCustomersPageChange={setCustomersPage}
      isLoadingCustomers={membersQuery.isLoading}
      tenantSlug={tenantSlug}
      locale={locale as Locales}
      deleteDialogOpen={deleteDialogOpen}
      onDeleteDialogOpenChange={setDeleteDialogOpen}
      removeMode={removeMode}
      dependentCampaignCount={dependentCampaignCount}
      isLoadingDependencies={audienceCampaignsQuery.isLoading}
      onDeleteConfirm={handleRemoveConfirm}
      isDeletingAudience={deleteAudience.isPending || archiveAudience.isPending}
    />
  );
}
